import { useEffect, useState } from "react";

import { Button, Column, OutlinedCard, Row, Text, useAppPalette } from "@bridgesplit/ui";
import { CircularProgress, Tooltip, useMediaQuery } from "@mui/material";
import {
    getLoopNotifyFormUrl,
    isLoopComingSoon,
    LoopExpanded,
    getLoopSlippage,
    useStrategyDurations
} from "@bridgesplit/abf-react";
import { formatDurationWithTypeShorthand, LoopRouteType, StrategyDuration } from "@bridgesplit/abf-sdk";
import { percentDecimalsToUi, percentUiToDecimals } from "@bridgesplit/utils";
import { DispatchType } from "@bridgesplit/react";
import { History, SwapHoriz } from "@mui/icons-material";
import { useSetLoopWindDuration } from "app/components/market-detail/borrow/util";
import { ActionsSettingsButton } from "app/components/loan/actions/common";
import { AUTO_SLIPPAGE, SlippageValue, formatSlippageRowEndText } from "app/components/common/slippage";
import { AppDialog, useAppDialog } from "app/utils";
import { usePermissionedLoanCheck } from "app/hooks/usePermissionedLoanCheck";
import { ComingSoonCard } from "app/components/common";

import { ManualWindCard } from "./ManualWind/ManualWindCard";
import WindAmount from "./WindAmount";
import WindMultiplier from "./WindMultiplier";
import WindStats from "./WindStats";
import WindCta from "./WindCta";
import WindNestedStats from "./WindNestedStats";
import { LOOP_DETAIL_QUERY } from "../detail";
import { useWindContext } from "./WindContext";
import { useLoopContext } from "../LoopContext";
import { WindForm, LoopMode } from "./types";

export function WindCard() {
    const isMobile = useMediaQuery(LOOP_DETAIL_QUERY.below);
    const { strategyDurations } = useStrategyDurations();
    const {
        form: { percentSlippageDecimals, strategyDuration },
        setForm,
        loopMode,
        setLoopMode
    } = useWindContext();
    const { loopExpanded } = useLoopContext();
    const { textSecondary } = useAppPalette();
    const { isPermissionedToken } = usePermissionedLoanCheck(
        loopExpanded?.principalToken,
        loopExpanded?.collateralToken
    );
    const { setLoopWindDuration } = useSetLoopWindDuration();
    const [isModeInitialized, setIsModeInitialized] = useState(false);
    const slippagePercentUi =
        percentSlippageDecimals !== undefined ? percentDecimalsToUi(percentSlippageDecimals) : AUTO_SLIPPAGE;
    const isAutoEnabled =
        !!loopExpanded && loopExpanded.loopVault.routeType !== LoopRouteType.Manual && !isPermissionedToken;
    const isModeLoading = !loopExpanded || !isModeInitialized;

    useEffect(() => {
        setIsModeInitialized(false);
    }, [loopExpanded?.vaultIdentifier]);

    useEffect(() => {
        if (!loopExpanded || isModeInitialized) return;
        setLoopMode(isAutoEnabled ? LoopMode.Auto : LoopMode.Manual);
        setIsModeInitialized(true);
    }, [loopExpanded, isModeInitialized, isAutoEnabled, setLoopMode]);

    if (!loopExpanded) {
        return (
            <OutlinedCard hideCard={isMobile} spacing={3}>
                <Row sx={{ width: "100%", minHeight: 200, alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={24} sx={{ color: textSecondary }} />
                </Row>
            </OutlinedCard>
        );
    }

    if (isLoopComingSoon(loopExpanded)) {
        return (
            <ComingSoonCard
                isMobile={isMobile}
                label="This Market is not yet live"
                notifyFormUrl={getLoopNotifyFormUrl(loopExpanded)}
            />
        );
    }

    return (
        <Column spacing={3}>
            <Column spacing={1}>
                <OutlinedCard hideCard={isMobile} spacing={3}>
                    <WindModeSwitchHeader
                        value={loopMode}
                        setValue={setLoopMode}
                        isAutoEnabled={isAutoEnabled}
                        isLoading={isModeLoading}
                        loopVaultName={loopExpanded?.loopVault.name}
                    />
                    {isModeLoading ? (
                        <Row
                            sx={{
                                width: "100%",
                                minHeight: 200,
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <CircularProgress size={24} sx={{ color: textSecondary }} />
                        </Row>
                    ) : loopMode === LoopMode.Manual ? (
                        <ManualWindCard />
                    ) : (
                        <AutoWindCard
                            slippagePercentUi={slippagePercentUi}
                            percentSlippageDecimals={percentSlippageDecimals}
                            strategyDuration={strategyDuration}
                            strategyDurations={strategyDurations}
                            loopExpanded={loopExpanded}
                            setForm={setForm}
                            setLoopWindDuration={setLoopWindDuration}
                        />
                    )}
                </OutlinedCard>
            </Column>

            <WindStats />
        </Column>
    );
}

function WindModeSwitchHeader({
    value,
    setValue,
    isAutoEnabled,
    isLoading,
    loopVaultName
}: {
    value: LoopMode;
    setValue: DispatchType<LoopMode>;
    isAutoEnabled: boolean;
    isLoading: boolean;
    loopVaultName: string | undefined;
}) {
    const isToggleDisabled = isLoading || !isAutoEnabled;
    const tooltipText =
        !isLoading && !isAutoEnabled
            ? `Auto Looping is not currently enabled for ${loopVaultName ?? "this loop"}`
            : undefined;

    const handleToggle = () => {
        if (isToggleDisabled) return;
        setValue(value === LoopMode.Manual ? LoopMode.Auto : LoopMode.Manual);
    };

    return (
        <Row spaceBetween>
            <Text variant="h4">Open position</Text>
            {!isLoading && (
                <Tooltip title={tooltipText ?? ""}>
                    <span>
                        <Button variant="outlined" onClick={handleToggle} disabled={isToggleDisabled}>
                            <Row spacing={0.5} sx={{ alignItems: "center" }}>
                                <Text variant="body1">{value === LoopMode.Manual ? "Manual" : "Auto"}</Text>
                                <SwapHoriz fontSize="small" />
                            </Row>
                        </Button>
                    </span>
                </Tooltip>
            )}
        </Row>
    );
}

function AutoWindCard({
    slippagePercentUi,
    percentSlippageDecimals,
    strategyDuration,
    strategyDurations,
    loopExpanded,
    setForm,
    setLoopWindDuration
}: {
    slippagePercentUi: SlippageValue;
    percentSlippageDecimals: number | undefined;
    strategyDuration: StrategyDuration;
    strategyDurations: StrategyDuration[] | undefined;
    loopExpanded: LoopExpanded | undefined;
    setForm: DispatchType<WindForm>;
    setLoopWindDuration: (strategyDuration: StrategyDuration) => void;
}) {
    const { open } = useAppDialog();

    return (
        <>
            <WindAmount />
            <Column>
                <Row spaceBetween>
                    <Text variant="body2" color="caption">
                        Leverage
                    </Text>
                    <Row spacing={0.5}>
                        <ActionsSettingsButton
                            rowEndText={formatSlippageRowEndText(slippagePercentUi, percentSlippageDecimals)}
                            onClick={() => {
                                open(AppDialog.SlippageSettings, {
                                    value: slippagePercentUi,
                                    showAuto: false,
                                    presets: getLoopSlippage(loopExpanded).slippagePresetsUi,
                                    setValue: (value) => {
                                        setForm((prev) => ({
                                            ...prev,
                                            percentSlippageDecimals:
                                                typeof value === "number" ? percentUiToDecimals(value) : undefined
                                        }));
                                    }
                                });
                            }}
                        />
                        <ActionsSettingsButton
                            icon={<History fontSize="inherit" sx={{ color: "secondary.main" }} />}
                            rowEndText={`${formatDurationWithTypeShorthand(
                                strategyDuration.duration,
                                strategyDuration.durationType
                            )}`}
                            onClick={() => {
                                open(AppDialog.WindSetTerm, {
                                    strategyDurations,
                                    strategyDuration,
                                    setStrategyDuration: setLoopWindDuration
                                });
                            }}
                        />
                    </Row>
                </Row>
                <WindMultiplier />
            </Column>
            <WindNestedStats />
            <WindCta />
        </>
    );
}
